import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import {
  adminDeviceFilterParams,
  useAdminDevices,
  useAdminDeviceTotal,
  useReassignDevice,
  type AdminDeviceFilters,
  type AdminDeviceSort,
  type AdminDeviceSearchField,
  type AdminDeviceStatusFilter,
} from "../../api/admin";
import {
  DEVICE_DATE_RANGE_FILTERS,
  deviceDateFilters,
  useAdminCreateDevice,
  useAdminDeleteDevice,
  useAdminUpdateDevice,
} from "../../api/devices";
import type { DayRange } from "../../lib/dayRange";
import { useAdminDeviceModels, useAdminDeviceTypes } from "../../api/deviceTypes";
import { deviceModelLabel } from "../../api/deviceModelLabel";
import { extractErrorMessage } from "../../api/errors";
import { useCsvExport } from "../../api/exports";
import { useAdminGroupsForTenant, useAdminGroupsForTenants, useGroups } from "../../api/groups";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { formatRange, totalPagesFor, useCursorPaging } from "../../hooks/useCursorPaging";
import { useAdminCompanies } from "../../api/admin";
import { modelOptionsWithCurrent } from "./deviceModelOptions";
import { DataTable, type ServerSort } from "../../components/DataTable";
import { StatusBadge } from "../../components/StatusBadge";
import type { Company, Device } from "../../api/types";
import { ChevronDown, Download, Move as MoveIcon, Pencil, Plus, Rocket, Search, Settings2, Trash2, Upload, X } from "lucide-react";

type AdminDevice = Device & {
  tenantId: string;
  tenantName?: string;
  groupName?: string;
};

interface AdminDevicesPageProps {
  scopedTenantId?: string;
  scopedCompanyName?: string;
}

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const SEARCH_DEBOUNCE_MS = 300;
// The backend accepts at most this many tenant IDs per request.
const MAX_FILTER_TENANTS = 100;

// Table filter state as the DataTable reports it. "tenantName" is resolved
// to tenant IDs here, since the server only knows tenant IDs.
interface TableFilterState {
  search: string;
  searchField: AdminDeviceSearchField | "tenantName";
  status: AdminDeviceStatusFilter | "";
  tenantIds: string[];
  unallocated: boolean;
  dateRanges: Record<string, DayRange>;
}

const EMPTY_TABLE_FILTERS: TableFilterState = {
  search: "",
  searchField: "imei",
  status: "",
  tenantIds: [],
  unallocated: false,
  dateRanges: {},
};

interface CompanyPickerProps {
  inputId: string;
  companies: Company[];
  selectedTenantId: string;
  query: string;
  open: boolean;
  onQueryChange: (value: string) => void;
  onOpenChange: (value: boolean) => void;
  onSelect: (company: Company) => void;
}

function CompanyPicker({
  inputId,
  companies,
  selectedTenantId,
  query,
  open,
  onQueryChange,
  onOpenChange,
  onSelect,
}: CompanyPickerProps) {
  const normalizedQuery = query.trim().toLowerCase();
  const selectedCompany = companies.find((company) => company.tenantId === selectedTenantId);
  const filteredCompanies = normalizedQuery
    ? companies.filter((company) =>
        [
          company.companyName,
          company.tenantId,
          company.contactEmail,
        ].some((value) => value?.toLowerCase().includes(normalizedQuery)),
      )
    : companies;

  return (
    <div className="relative">
      <div className="relative">
        <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-riot-navy-400" />
        <input
          id={inputId}
          type="text"
          value={query}
          onChange={(e) => {
            onQueryChange(e.target.value);
            onOpenChange(true);
          }}
          onFocus={() => onOpenChange(true)}
          onClick={() => onOpenChange(true)}
          onMouseDown={() => onOpenChange(true)}
          placeholder={selectedCompany?.companyName ?? "Search companies"}
          autoComplete="off"
          className="w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-8 text-sm text-gray-900 placeholder:text-gray-400 focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500 dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
        />
        <ChevronDown
          size={15}
          className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 transition-transform dark:text-riot-navy-400 ${open ? "rotate-180" : ""}`}
        />
      </div>
      {open && (
        <div className="absolute left-0 right-0 z-30 mt-1 max-h-72 overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg dark:border-riot-navy-700 dark:bg-riot-navy-800">
          {filteredCompanies.length === 0 ? (
            <div className="px-3 py-2 text-xs text-gray-400 dark:text-riot-navy-400">No companies found</div>
          ) : (
            filteredCompanies.map((company) => (
              <button
                key={company.tenantId}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  onSelect(company);
                }}
                className={`block w-full px-3 py-2 text-left text-xs hover:bg-gray-50 dark:hover:bg-riot-navy-700 ${
                  company.tenantId === selectedTenantId ? "bg-riot-gold-50 text-riot-navy-900 dark:bg-riot-gold-500/10 dark:text-riot-gold-400" : "text-gray-700 dark:text-riot-navy-200"
                }`}
              >
                <span className="block font-medium">{company.companyName}</span>
                <span className="mt-0.5 block truncate font-mono text-[11px] text-gray-400 dark:text-riot-navy-400">
                  {company.tenantId}
                  {company.contactEmail ? ` · ${company.contactEmail}` : ""}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export function AdminDevicesPage({ scopedTenantId, scopedCompanyName }: AdminDevicesPageProps = {}) {
  const navigate = useNavigate();
  const isScopedCompanyView = !!scopedTenantId;
  const [tableFilters, setTableFilters] = useState<TableFilterState>(EMPTY_TABLE_FILTERS);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  // Null restores the server's default IMEI order.
  const [sort, setSort] = useState<AdminDeviceSort | null>(null);
  const debouncedSearch = useDebouncedValue(tableFilters.search, SEARCH_DEBOUNCE_MS);
  const scopedGroupsQuery = useAdminGroupsForTenant(scopedTenantId ?? "");
  const ownGroupsQuery = useGroups({ counts: false });
  const groupsQuery = isScopedCompanyView ? scopedGroupsQuery : ownGroupsQuery;
  const companiesQuery = useAdminCompanies({ enabled: !isScopedCompanyView });
  const deviceTypesQuery = useAdminDeviceTypes();
  const createDevice = useAdminCreateDevice();
  const updateDevice = useAdminUpdateDevice();
  const deleteDevice = useAdminDeleteDevice();
  const reassign = useReassignDevice();

  const [selectedImeis, setSelectedImeis] = useState<string[]>([]);
  const [showAddDevice, setShowAddDevice] = useState(false);
  const [tenantId, setTenantId] = useState("");
  const [companyQuery, setCompanyQuery] = useState("");
  const [companyPickerOpen, setCompanyPickerOpen] = useState(false);
  const [imei, setImei] = useState("");
  const [deviceName, setDeviceName] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [editDevice, setEditDevice] = useState<AdminDevice | null>(null);
  const [editTenantId, setEditTenantId] = useState("");
  const [editCompanyQuery, setEditCompanyQuery] = useState("");
  const [editCompanyPickerOpen, setEditCompanyPickerOpen] = useState(false);
  const [editDeviceName, setEditDeviceName] = useState("");
  const [editContactNumber, setEditContactNumber] = useState("");
  const [editGroupId, setEditGroupId] = useState("");
  const [editDeviceTypeId, setEditDeviceTypeId] = useState("");
  const [editModel, setEditModel] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editError, setEditError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<AdminDevice | null>(null);
  const [deleteError, setDeleteError] = useState("");
  const [deviceTypeId, setDeviceTypeId] = useState("");
  const [model, setModel] = useState("");
  const [groupId, setGroupId] = useState("");
  const [description, setDescription] = useState("");
  const [addError, setAddError] = useState("");
  const [moveOpen, setMoveOpen] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  const moveRef = useRef<HTMLDivElement>(null);
  const companyPickerRef = useRef<HTMLDivElement>(null);
  const editCompanyPickerRef = useRef<HTMLDivElement>(null);

  const deviceModelsQuery = useAdminDeviceModels({ deviceTypeId: deviceTypeId || undefined, enabled: !!deviceTypeId });
  const selectedCompanyGroupsQuery = useAdminGroupsForTenant(tenantId);
  const editDeviceModelsQuery = useAdminDeviceModels({ deviceTypeId: editDeviceTypeId || undefined, enabled: !!editDeviceTypeId });
  // All models (unfiltered) so the device table can show friendly model names
  // across every device type, not just the one being edited.
  const allDeviceModels = useAdminDeviceModels().data?.data ?? [];
  const editCompanyGroupsQuery = useAdminGroupsForTenant(editTenantId);

  // The filters the server applies. Searching by company name is resolved
  // to tenant IDs from the full company list; `blocked` explains why no
  // request can match (or can't be sent yet) instead of querying.
  const serverQuery = useMemo((): { filters: AdminDeviceFilters | null; blocked?: string } => {
    const term = debouncedSearch.trim();
    let tenantIds = isScopedCompanyView && scopedTenantId ? [scopedTenantId] : tableFilters.tenantIds;
    let search = term;
    let searchField: AdminDeviceSearchField | undefined =
      tableFilters.searchField === "tenantName" ? undefined : tableFilters.searchField;

    if (tableFilters.searchField === "tenantName" && term) {
      if (companiesQuery.isError) return { filters: null, blocked: "Couldn't load companies to search by name. Try again." };
      if (companiesQuery.isLoading) return { filters: null };
      const lower = term.toLowerCase();
      const named = (companiesQuery.data?.data ?? [])
        .filter((company) => company.companyName.toLowerCase().includes(lower))
        .map((company) => company.tenantId);
      tenantIds = tenantIds.length > 0 ? tenantIds.filter((id) => named.includes(id)) : named;
      if (tenantIds.length === 0) return { filters: null, blocked: `No company matches "${term}".` };
      search = "";
      searchField = undefined;
    }
    if (tenantIds.length > MAX_FILTER_TENANTS) {
      return { filters: null, blocked: `More than ${MAX_FILTER_TENANTS} companies match. Narrow the company search or selection.` };
    }

    return {
      filters: {
        search,
        searchField,
        tenantIds,
        unallocated: tableFilters.unallocated,
        status: tableFilters.status,
        ...deviceDateFilters(tableFilters.dateRanges),
      },
    };
  }, [companiesQuery.data?.data, companiesQuery.isError, companiesQuery.isLoading, debouncedSearch, isScopedCompanyView, scopedTenantId, tableFilters]);

  // Paging restarts whenever the filters, page size or sort change, so the
  // debounced search can't resume a stale position.
  const paging = useCursorPaging(JSON.stringify([serverQuery.filters, pageSize, sort]));

  const canQuery = serverQuery.filters !== null;
  const adminDevicesQuery = useAdminDevices({
    enabled: canQuery,
    filters: serverQuery.filters ?? undefined,
    sort,
    limit: pageSize,
    cursor: paging.cursor,
  });
  const totalQuery = useAdminDeviceTotal({ enabled: canQuery, filters: serverQuery.filters ?? undefined });

  const adminDevices = useMemo<AdminDevice[]>(
    () => (canQuery ? adminDevicesQuery.data?.data ?? [] : []),
    [adminDevicesQuery.data?.data, canQuery],
  );
  const isLoading = canQuery && adminDevicesQuery.isLoading;
  const adminDeviceTenantIds = useMemo(
    () => Array.from(new Set(adminDevices.map((device) => device.tenantId).filter(Boolean))).sort(),
    [adminDevices],
  );
  const adminGroupQueries = useAdminGroupsForTenants(adminDeviceTenantIds);

  function showFlash(message: string) {
    setFlash(message);
    setTimeout(() => setFlash(null), 3500);
  }

  // Exports every device matching the table's filters, across all the
  // companies they cover, on the server.
  const csvExport = useCsvExport("admin_devices", {
    onDone: (job) => showFlash(`Exported ${job.rowCount.toLocaleString()} device${job.rowCount === 1 ? "" : "s"} to CSV.`),
    onError: showFlash,
  });
  const exportProgress = csvExport.running
    ? `Preparing CSV export…${csvExport.rowCount > 0 ? ` ${csvExport.rowCount.toLocaleString()} devices so far` : ""}`
    : null;

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (moveRef.current && !moveRef.current.contains(e.target as Node)) {
        setMoveOpen(false);
      }
    }
    if (moveOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [moveOpen]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (companyPickerRef.current && !companyPickerRef.current.contains(e.target as Node)) {
        setCompanyPickerOpen(false);
      }
    }
    if (companyPickerOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [companyPickerOpen]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (editCompanyPickerRef.current && !editCompanyPickerRef.current.contains(e.target as Node)) {
        setEditCompanyPickerOpen(false);
      }
    }
    if (editCompanyPickerOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [editCompanyPickerOpen]);

  useEffect(() => {
    if (!scopedTenantId) return;
    setTenantId(scopedTenantId);
    setCompanyQuery(scopedCompanyName ?? "Current company");
  }, [scopedCompanyName, scopedTenantId]);

  async function applyMove(target: { groupId?: string | null; tenantId?: string }, label: string) {
    if (selectedImeis.length === 0) return;
    setMoveOpen(false);
    const effectiveTarget = scopedTenantId && !target.tenantId
      ? { ...target, tenantId: scopedTenantId }
      : target;
    try {
      await Promise.all(
        selectedImeis.map((imei) => reassign.mutateAsync({ imei, ...effectiveTarget })),
      );
      showFlash(`Moved ${selectedImeis.length} device${selectedImeis.length === 1 ? "" : "s"} to ${label}.`);
      setSelectedImeis([]);
    } catch {
      showFlash("Move failed — see console for details.");
    }
  }

  async function handleAddDevice(e: FormEvent) {
    e.preventDefault();
    setAddError("");
    const trimmedName = deviceName.trim();
    try {
      await createDevice.mutateAsync({
        tenantId,
        body: {
          imei: imei.trim(),
          deviceName: trimmedName || undefined,
          contactNumber: contactNumber.trim() || undefined,
          deviceTypeId: deviceTypeId || undefined,
          model: model || undefined,
          groupId: groupId || undefined,
          description: description.trim() || undefined,
        },
      });
      const companyName = scopedCompanyName ?? companies.find((company) => company.tenantId === tenantId)?.companyName ?? "selected company";
      showFlash(`Added ${imei.trim()} to ${companyName}.`);
      setShowAddDevice(false);
      setTenantId(scopedTenantId ?? "");
      setCompanyQuery(scopedCompanyName ?? "");
      setCompanyPickerOpen(false);
      setImei("");
      setDeviceName("");
      setContactNumber("");
      setDeviceTypeId("");
      setModel("");
      setGroupId("");
      setDescription("");
    } catch (err) {
      setAddError(extractErrorMessage(err, "Failed to add device"));
    }
  }

  function startEditDevice(device: AdminDevice) {
    setEditDevice(device);
    setEditTenantId(device.tenantId);
    // Leave the picker's search query EMPTY, not pre-filled with the current
    // company name — the query doubles as the picker's filter, so pre-filling it
    // narrows the list to the current company (or to nothing when the name
    // doesn't match a loaded company), making reassignment impossible. The
    // current company is still shown via the selected-tenant placeholder and the
    // highlighted option; an empty query lists every company to move to.
    setEditCompanyQuery("");
    setEditCompanyPickerOpen(false);
    setEditDeviceName(device.deviceName && device.deviceName !== device.imei ? device.deviceName : "");
    setEditContactNumber(device.contactNumber ?? "");
    setEditGroupId(device.groupId ?? "");
    setEditDeviceTypeId(device.deviceTypeId ?? "");
    setEditModel(device.model ?? "");
    setEditDescription(device.description ?? "");
    setEditError("");
  }

  function resetEditDevice() {
    setEditDevice(null);
    setEditTenantId("");
    setEditCompanyQuery("");
    setEditCompanyPickerOpen(false);
    setEditDeviceName("");
    setEditContactNumber("");
    setEditGroupId("");
    setEditDeviceTypeId("");
    setEditModel("");
    setEditDescription("");
    setEditError("");
  }

  async function handleSaveEditDevice(e: FormEvent) {
    e.preventDefault();
    if (!editDevice) return;
    setEditError("");
    // IMEI is a device's hardware identity — never editable, so it's submitted
    // unchanged.
    const deviceImei = editDevice.imei;
    const trimmedName = editDeviceName.trim();
    if (!editTenantId) {
      setEditError("Company is required.");
      return;
    }
    try {
      if (editTenantId !== editDevice.tenantId) {
        await reassign.mutateAsync({
          imei: editDevice.imei,
          tenantId: editTenantId,
          groupId: editGroupId || null,
        });
      }
      await updateDevice.mutateAsync({
        tenantId: editTenantId,
        imei: editDevice.imei,
        body: {
          imei: deviceImei,
          deviceName: trimmedName,
          contactNumber: editContactNumber.trim() || null,
          groupId: editGroupId || "",
          deviceTypeId: editDeviceTypeId || null,
          model: editDeviceTypeId ? editModel || null : null,
          description: editDescription.trim(),
        },
      });
      showFlash(`Updated ${deviceImei}.`);
      resetEditDevice();
    } catch (err) {
      setEditError(extractErrorMessage(err, "Failed to update device"));
    }
  }

  async function handleDeleteDevice() {
    if (!deleteTarget) return;
    setDeleteError("");
    try {
      await deleteDevice.mutateAsync({
        tenantId: deleteTarget.tenantId,
        imei: deleteTarget.imei,
      });
      showFlash(`Deleted ${deleteTarget.deviceName || deleteTarget.imei}.`);
      setSelectedImeis((current) => current.filter((imei) => imei !== deleteTarget.imei));
      setDeleteTarget(null);
    } catch (err) {
      setDeleteError(extractErrorMessage(err, "Failed to delete device"));
    }
  }

  function handleCreateTaskForSelection() {
    if (selectedImeis.length === 0) {
      showFlash("Select one or more devices first.");
      return;
    }

    const selectedDevices = adminDevices.filter((device) => selectedImeis.includes(device.imei));
    const tenantIds = Array.from(new Set(selectedDevices.map((device) => device.tenantId).filter(Boolean)));

    if (tenantIds.length !== 1) {
      showFlash("Select devices from one company to create a task.");
      return;
    }

    const targetTenantId = tenantIds[0];
    const params = new URLSearchParams({
      tenantId: targetTenantId,
      imeis: selectedImeis.join(","),
    });
    navigate(`/tasks/create?${params.toString()}`);
  }

  function handleUploadFileForSelection() {
    if (selectedImeis.length === 0) {
      showFlash("Select one or more devices first.");
      return;
    }

    const selectedDevices = adminDevices.filter((device) => selectedImeis.includes(device.imei));
    const tenantIds = Array.from(new Set(selectedDevices.map((device) => device.tenantId).filter(Boolean)));

    if (tenantIds.length !== 1) {
      showFlash("Select devices from one company to add a file.");
      return;
    }

    const targetTenantId = tenantIds[0];
    const params = new URLSearchParams({
      tenantId: targetTenantId,
      imeis: selectedImeis.join(","),
    });
    navigate(`/firmware/upload?${params.toString()}`);
  }

  function goToShadowSettingsForDevice(device: AdminDevice) {
    if (!device.tenantId) {
      showFlash("Assign this device to a company before editing shadow settings.");
      return;
    }

    if (isScopedCompanyView) {
      const params = new URLSearchParams({ returnTo: "/devices" });
      navigate(`/devices/${encodeURIComponent(device.imei)}/shadow-settings?${params.toString()}`);
      return;
    }

    const params = new URLSearchParams({
      tenantId: device.tenantId,
      returnTo: "/admin/devices",
    });
    navigate(`/admin/devices/${encodeURIComponent(device.imei)}/shadow-settings?${params.toString()}`);
  }

  function handleShadowSettingsForSelection() {
    if (selectedImeis.length !== 1) {
      showFlash("Select exactly one device to edit shadow settings.");
      return;
    }

    const selectedDevice = adminDevices.find((device) => device.imei === selectedImeis[0]);
    if (!selectedDevice) {
      showFlash("Selected device was not found.");
      return;
    }

    goToShadowSettingsForDevice(selectedDevice);
  }

  const groups = groupsQuery.data?.data ?? [];
  const companies = useMemo(() => companiesQuery.data?.data ?? [], [companiesQuery.data?.data]);
  const companyNameByTenantId = useMemo(
    () => {
      const names = new Map(companies.map((company) => [company.tenantId, company.companyName]));
      if (scopedTenantId) {
        names.set(scopedTenantId, scopedCompanyName ?? "Current company");
      }
      return names;
    },
    [companies, scopedCompanyName, scopedTenantId],
  );
  const groupNameByTenantAndId = useMemo(() => {
    const names = new Map<string, string>();
    adminGroupQueries.forEach((query, index) => {
      const rowTenantId = adminDeviceTenantIds[index];
      for (const group of query.data?.data ?? []) {
        names.set(`${rowTenantId}:${group.groupId}`, group.name);
      }
    });
    return names;
  }, [adminGroupQueries, adminDeviceTenantIds]);
  const loadingGroupTenantIds = useMemo(() => {
    const ids = new Set<string>();
    adminGroupQueries.forEach((query, index) => {
      if (query.isLoading) ids.add(adminDeviceTenantIds[index]);
    });
    return ids;
  }, [adminGroupQueries, adminDeviceTenantIds]);
  const tableData = useMemo(
    () =>
      adminDevices.map((device) => ({
        ...device,
        tenantName: companyNameByTenantId.get(device.tenantId) ?? (companiesQuery.isLoading ? "Loading..." : "Unknown tenant"),
        groupName: device.groupId
          ? groupNameByTenantAndId.get(`${device.tenantId}:${device.groupId}`) ??
            (loadingGroupTenantIds.has(device.tenantId) ? "Loading..." : "Unknown group")
          : undefined,
      })),
    [adminDevices, companiesQuery.isLoading, companyNameByTenantId, groupNameByTenantAndId, loadingGroupTenantIds],
  );

  const columns = [
    { key: "status", header: "Status", serverSortable: true, initialSortDirection: "asc" as const, width: "100px", render: (row: AdminDevice) => <StatusBadge status={row.status} variant="dot" /> },
    { key: "lastSeen", header: "Last Seen", serverSortable: true, width: "150px", render: (row: AdminDevice) => row.lastSeen ? new Date(row.lastSeen).toLocaleString() : <span className="text-gray-400 dark:text-riot-navy-400">-</span> },
    { key: "deviceName", header: "Name", serverSortable: true, initialSortDirection: "asc" as const, width: "170px", render: (row: AdminDevice) => row.deviceName || <span className="text-gray-400 dark:text-riot-navy-400">-</span> },
    { key: "contactNumber", header: "Contact", sortable: true, width: "100px", render: (row: AdminDevice) => row.contactNumber || <span className="text-gray-400 dark:text-riot-navy-400">-</span> },
    { key: "firmwareVersion", header: "Firmware", serverSortable: true, initialSortDirection: "asc" as const, width: "100px", render: (row: AdminDevice) => row.firmwareVersion || <span className="text-gray-400 dark:text-riot-navy-400">-</span> },
    { key: "createdAt", header: "Added", serverSortable: true, width: "150px", render: (row: AdminDevice) => row.createdAt ? new Date(row.createdAt).toLocaleString() : <span className="text-gray-400 dark:text-riot-navy-400">-</span> },
    { key: "tenantName", header: "Tenant", sortable: true, width: "130px", render: (row: AdminDevice) => <span title={row.tenantId}>{row.tenantName}</span> },
    { key: "groupName", header: "Group", serverSortable: true, initialSortDirection: "asc" as const, width: "150px", render: (row: AdminDevice) => row.groupId ? <span title={row.groupId}>{row.groupName}</span> : <span className="text-gray-400 dark:text-riot-navy-400">—</span> },
    {
      key: "deviceInfo",
      header: "Device info",
      width: "200px",
      render: (row: AdminDevice) => (
        <div className="space-y-0.5 text-xs leading-4">
          <div>
            <span className="text-gray-400 dark:text-riot-navy-400">IMEI: </span>
            <span className="font-mono font-medium text-gray-800 dark:text-riot-navy-100">{row.imei}</span>
          </div>
          <div>
            <span className="text-gray-400 dark:text-riot-navy-400">Model: </span>
            <span className="font-medium text-gray-700 dark:text-riot-navy-200">{deviceModelLabel(row.model, allDeviceModels) || "-"}</span>
          </div>
        </div>
      ),
    },
    {
      key: "manage",
      header: "Manage",
      width: "220px",
      render: (row: AdminDevice) => (
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              startEditDevice(row);
            }}
            className="flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
            title="Edit device"
          >
            <Pencil size={13} /> Edit
          </button>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              setDeleteTarget(row);
              setDeleteError("");
            }}
            className="flex items-center gap-1 rounded-md border border-red-300 px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-50 dark:border-red-900/60 dark:text-red-300 dark:hover:bg-red-950/30"
            title="Delete device"
          >
            <Trash2 size={13} /> Delete
          </button>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              goToShadowSettingsForDevice(row);
            }}
            disabled={!row.tenantId}
            className="flex items-center gap-1 rounded-md border border-riot-gold-500/60 px-2.5 py-1 text-xs font-medium text-riot-gold-500 hover:bg-riot-gold-500/10 disabled:cursor-not-allowed disabled:opacity-45"
            title={row.tenantId ? "Edit shadow settings" : "Assign this device to a company first"}
          >
            <Settings2 size={13} /> Shadow
          </button>
        </div>
      ),
    },
  ];
  const visibleColumns = isScopedCompanyView
    ? columns.filter((column) => column.key !== "tenantName")
    : columns;

  const deviceTypes = [...(deviceTypesQuery.data?.data ?? [])]
    .filter((type) => type.status !== "deprecated")
    .sort((a, b) => a.name.localeCompare(b.name));
  const deviceModels = [...(deviceModelsQuery.data?.data ?? [])]
    .filter((entry) => entry.status !== "deprecated")
    .sort((a, b) => a.name.localeCompare(b.name));
  const editDeviceModels = [...(editDeviceModelsQuery.data?.data ?? [])]
    .filter((entry) => entry.status !== "deprecated")
    .sort((a, b) => a.name.localeCompare(b.name));
  // Keep the device's current model selectable even when it isn't a catalog
  // entry (fleet units report free-form model strings) — otherwise the form
  // silently drops it and saving wipes the model. See modelOptionsWithCurrent.
  const editModelOptions = modelOptionsWithCurrent(editModel, editDeviceModels);
  const selectedCompanyGroups = [...(selectedCompanyGroupsQuery.data?.data ?? [])]
    .sort((a, b) => a.name.localeCompare(b.name));
  const editCompanyGroups = [...(editCompanyGroupsQuery.data?.data ?? [])]
    .sort((a, b) => a.name.localeCompare(b.name));
  const companyFilterOptions = companies.map((company) => ({
    value: company.tenantId,
    label: company.companyName,
  }));
  const tableSearchFields = [
    { value: "imei", label: "IMEI" },
    { value: "deviceName", label: "Name" },
    { value: "contactNumber", label: "Contact" },
    ...(!isScopedCompanyView ? [{ value: "tenantName", label: "Tenant" }] : []),
    { value: "model", label: "Model" },
  ];
  const tableMultiSelectFilters = isScopedCompanyView
    ? undefined
    : [
        {
          key: "tenantId",
          label: "Companies",
          options: companyFilterOptions,
        },
      ];

  // Totals come from the server's count for the current filters — never
  // from the rows on screen, which are one page of the result.
  // While a new filter's count is in flight, show "…" rather than the
  // previous filter's total.
  const total = !canQuery ? 0 : totalQuery.isPlaceholderData ? undefined : totalQuery.data?.total;
  const totalIsLowerBound = canQuery && !totalQuery.isPlaceholderData && !!totalQuery.data?.isLowerBound;
  const hasMore = canQuery && !!adminDevicesQuery.data?.pagination.hasMore;
  const totalPages = totalPagesFor(paging.page, pageSize, hasMore, total);
  const rangeLabel = formatRange(paging.offset, adminDevices.length, total, totalIsLowerBound);

  // Selection belongs to the page on screen: bulk actions (Move, Create
  // task, Add file) must never act on rows the admin can no longer see.
  function showNextPage() {
    const next = adminDevicesQuery.data?.pagination.cursor;
    if (!next || adminDevicesQuery.isPlaceholderData) return;
    setSelectedImeis([]);
    paging.next(next, adminDevices.length);
  }

  function showPreviousPage() {
    setSelectedImeis([]);
    paging.prev();
  }

  function restartPaging() {
    setSelectedImeis([]);
    paging.first();
  }

  // A sparse search can use up the server's per-request budget before it
  // finds a match. That's an empty page with more to check — not "no
  // results" — so offer to carry on.
  const emptyMessage =
    canQuery && hasMore && adminDevices.length === 0 && !adminDevicesQuery.isPlaceholderData ? (
      <span>
        No matches in the devices checked so far.{" "}
        <button
          type="button"
          onClick={showNextPage}
          className="font-medium text-riot-gold-600 hover:text-riot-gold-700 dark:text-riot-gold-400"
        >
          Keep searching
        </button>
      </span>
    ) : undefined;

  return (
    <div>
      {(flash ?? exportProgress) && (
        <div
          role="status"
          className="fixed bottom-6 right-6 z-50 rounded-md bg-riot-navy-900 px-4 py-2 text-xs text-white shadow-lg dark:bg-riot-gold-400 dark:text-riot-navy-900"
        >
          {flash ?? exportProgress}
        </div>
      )}
      {isLoading ? (
        <div className="py-20 text-center text-sm text-gray-400 dark:text-riot-navy-400">Loading all devices...</div>
      ) : (
        <div className="space-y-4">
          {deleteTarget && (
            <div className="rounded-lg border border-red-300 bg-red-50 p-4 dark:border-red-900/50 dark:bg-red-950/20">
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-red-800 dark:text-red-300">Delete device?</h3>
                  <p className="mt-1 text-xs text-red-700 dark:text-red-300/80">
                    {deleteTarget.deviceName || deleteTarget.imei} will be soft-deleted and removed from device lists.
                  </p>
                  <p className="mt-1 font-mono text-[11px] text-red-700/80 dark:text-red-300/70">
                    IMEI {deleteTarget.imei}
                  </p>
                  {deleteError && <p className="mt-2 text-xs text-red-700 dark:text-red-300">{deleteError}</p>}
                </div>
                <div className="flex shrink-0 gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setDeleteTarget(null);
                      setDeleteError("");
                    }}
                    className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-white dark:border-riot-navy-600 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleDeleteDevice()}
                    disabled={deleteDevice.isPending}
                    className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 active:bg-red-800 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {deleteDevice.isPending ? "Deleting..." : "Delete device"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {editDevice && (
            <form
              onSubmit={(e) => void handleSaveEditDevice(e)}
              className="rounded-lg border border-gray-200 bg-white p-4 dark:border-riot-navy-700 dark:bg-riot-navy-800"
            >
              <div className="mb-3 flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Edit device</h3>
                  <p className="mt-1 text-xs text-gray-500 dark:text-riot-navy-300">
                    Update ownership, grouping, identifiers, and hardware catalog fields for this device.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={resetEditDevice}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-riot-navy-200"
                  aria-label="Close editor"
                >
                  <X size={14} />
                </button>
              </div>

              <div className="grid grid-cols-1 gap-3 lg:grid-cols-6 lg:items-end">
                <div className="lg:col-span-2">
                  <label htmlFor="admin-edit-company" className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                    Company
                  </label>
                  {isScopedCompanyView ? (
                    <div
                      id="admin-edit-company"
                      className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 dark:border-riot-navy-700 dark:bg-riot-navy-900/50 dark:text-riot-navy-200"
                    >
                      {scopedCompanyName ?? "Current company"}
                    </div>
                  ) : (
                    <div ref={editCompanyPickerRef}>
                      <CompanyPicker
                        inputId="admin-edit-company"
                        companies={companies}
                        selectedTenantId={editTenantId}
                        query={editCompanyQuery}
                        open={editCompanyPickerOpen}
                        onQueryChange={(value) => {
                          setEditCompanyQuery(value);
                          setEditTenantId("");
                          setEditGroupId("");
                        }}
                        onOpenChange={setEditCompanyPickerOpen}
                        onSelect={(company) => {
                          setEditTenantId(company.tenantId);
                          setEditCompanyQuery(company.companyName);
                          setEditCompanyPickerOpen(false);
                          setEditGroupId("");
                        }}
                      />
                    </div>
                  )}
                </div>
                <div className="lg:col-span-2">
                  <label htmlFor="admin-edit-group" className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                    Group
                  </label>
                  <select
                    id="admin-edit-group"
                    value={editGroupId}
                    onChange={(e) => setEditGroupId(e.target.value)}
                    disabled={!editTenantId || editCompanyGroupsQuery.isLoading}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 disabled:cursor-not-allowed disabled:opacity-60 dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
                  >
                    <option value="">
                      {!editTenantId
                        ? "Choose company first"
                        : editCompanyGroupsQuery.isLoading
                          ? "Loading groups..."
                          : "No group"}
                    </option>
                    {editCompanyGroups.map((group) => (
                      <option key={group.groupId} value={group.groupId}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="admin-edit-imei" className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                    IMEI
                  </label>
                  {/* A device's IMEI is its hardware identity — read-only, never
                      editable (even for platform admins). */}
                  <div
                    id="admin-edit-imei"
                    className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-sm text-gray-600 dark:border-riot-navy-700 dark:bg-riot-navy-900/50 dark:text-riot-navy-300"
                  >
                    {editDevice.imei}
                  </div>
                </div>
                <div>
                  <label htmlFor="admin-edit-device-name" className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                    Name
                  </label>
                  <input
                    id="admin-edit-device-name"
                    value={editDeviceName}
                    onChange={(e) => setEditDeviceName(e.target.value)}
                    placeholder="Device name"
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
                  />
                </div>
                <div>
                  <label htmlFor="admin-edit-contact-number" className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                    Contact number <span className="font-normal text-gray-400 dark:text-riot-navy-400">(optional)</span>
                  </label>
                  <input
                    id="admin-edit-contact-number"
                    type="tel"
                    value={editContactNumber}
                    onChange={(e) => setEditContactNumber(e.target.value)}
                    placeholder="+27 82 000 0000"
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
                  />
                </div>
                <div>
                  <label htmlFor="admin-edit-device-type" className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                    Type
                  </label>
                  <select
                    id="admin-edit-device-type"
                    value={editDeviceTypeId}
                    onChange={(e) => {
                      setEditDeviceTypeId(e.target.value);
                      setEditModel("");
                    }}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
                  >
                    <option value="">No type</option>
                    {deviceTypes.map((type) => (
                      <option key={type.deviceTypeId} value={type.deviceTypeId}>{type.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="admin-edit-device-model" className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                    Model
                  </label>
                  <select
                    id="admin-edit-device-model"
                    value={editModel}
                    onChange={(e) => setEditModel(e.target.value)}
                    disabled={!editDeviceTypeId}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 disabled:cursor-not-allowed disabled:opacity-60 dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
                  >
                    <option value="">No model</option>
                    {editModelOptions.map((entry) => (
                      <option key={entry.modelCode} value={entry.modelCode}>{entry.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mt-3">
                <label htmlFor="admin-edit-description" className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                  Description
                </label>
                <textarea
                  id="admin-edit-description"
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  rows={2}
                  placeholder="Optional notes"
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
                />
              </div>
              {editError && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{editError}</p>}
              <div className="mt-3 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={resetEditDevice}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateDevice.isPending || reassign.isPending || !editTenantId}
                  className="rounded-md bg-riot-gold-400 px-4 py-2 text-sm font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {updateDevice.isPending || reassign.isPending ? "Saving..." : "Save changes"}
                </button>
              </div>
            </form>
          )}

          {showAddDevice && (
            <form
              onSubmit={(e) => void handleAddDevice(e)}
              className="rounded-lg border border-gray-200 bg-white p-4 dark:border-riot-navy-700 dark:bg-riot-navy-800"
            >
              <div className="mb-3">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Add device</h3>
                <p className="mt-1 text-xs text-gray-500 dark:text-riot-navy-300">
                  Choose the company that owns this device. New devices are added without a group until they are moved.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-3 lg:grid-cols-6 lg:items-end">
                <div className="lg:col-span-2">
                  <label htmlFor="admin-device-company" className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                    Company
                  </label>
                  {isScopedCompanyView ? (
                    <div
                      id="admin-device-company"
                      className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 dark:border-riot-navy-700 dark:bg-riot-navy-900/50 dark:text-riot-navy-200"
                    >
                      {scopedCompanyName ?? "Current company"}
                    </div>
                  ) : (
                    <div ref={companyPickerRef}>
                      <CompanyPicker
                        inputId="admin-device-company"
                        companies={companies}
                        selectedTenantId={tenantId}
                        query={companyQuery}
                        open={companyPickerOpen}
                        onQueryChange={(value) => {
                          setCompanyQuery(value);
                          setTenantId("");
                          setGroupId("");
                        }}
                        onOpenChange={setCompanyPickerOpen}
                        onSelect={(company) => {
                          setTenantId(company.tenantId);
                          setCompanyQuery(company.companyName);
                          setCompanyPickerOpen(false);
                          setGroupId("");
                        }}
                      />
                    </div>
                  )}
                </div>
                <div className="lg:col-span-2">
                  <label htmlFor="admin-device-group" className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                    Group
                  </label>
                  <select
                    id="admin-device-group"
                    value={groupId}
                    onChange={(e) => setGroupId(e.target.value)}
                    disabled={!tenantId || selectedCompanyGroupsQuery.isLoading}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 disabled:cursor-not-allowed disabled:opacity-60 dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
                  >
                    <option value="">
                      {!tenantId
                        ? "Choose company first"
                        : selectedCompanyGroupsQuery.isLoading
                          ? "Loading groups..."
                          : "No group"}
                    </option>
                    {selectedCompanyGroups.map((group) => (
                      <option key={group.groupId} value={group.groupId}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="admin-device-imei" className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                    IMEI
                  </label>
                  <input
                    id="admin-device-imei"
                    value={imei}
                    onChange={(e) => setImei(e.target.value)}
                    required
                    placeholder="358..."
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
                  />
                </div>
                <div>
                  <label htmlFor="admin-device-name" className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                    Name
                  </label>
                  <input
                    id="admin-device-name"
                    value={deviceName}
                    onChange={(e) => setDeviceName(e.target.value)}
                    placeholder="Device name"
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
                  />
                </div>
                <div>
                  <label htmlFor="admin-device-contact-number" className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                    Contact number <span className="font-normal text-gray-400 dark:text-riot-navy-400">(optional)</span>
                  </label>
                  <input
                    id="admin-device-contact-number"
                    type="tel"
                    value={contactNumber}
                    onChange={(e) => setContactNumber(e.target.value)}
                    placeholder="+27 82 000 0000"
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
                  />
                </div>
                <div>
                  <label htmlFor="admin-device-type" className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                    Type
                  </label>
                  <select
                    id="admin-device-type"
                    value={deviceTypeId}
                    onChange={(e) => {
                      setDeviceTypeId(e.target.value);
                      setModel("");
                    }}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
                  >
                    <option value="">No type</option>
                    {deviceTypes.map((type) => (
                      <option key={type.deviceTypeId} value={type.deviceTypeId}>{type.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="admin-device-model" className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                    Model
                  </label>
                  <select
                    id="admin-device-model"
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    disabled={!deviceTypeId}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 disabled:cursor-not-allowed disabled:opacity-60 dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
                  >
                    <option value="">No model</option>
                    {deviceModels.map((entry) => (
                      <option key={entry.modelCode} value={entry.modelCode}>{entry.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mt-3">
                <label htmlFor="admin-device-description" className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                  Description
                </label>
                <textarea
                  id="admin-device-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                  placeholder="Optional notes"
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
                />
              </div>
              <div className="mt-3 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddDevice(false);
                    setAddError("");
                    setTenantId(scopedTenantId ?? "");
                    setCompanyQuery(scopedCompanyName ?? "");
                    setCompanyPickerOpen(false);
                    setGroupId("");
                    setImei("");
                    setDeviceName("");
                    setContactNumber("");
                    setDeviceTypeId("");
                    setModel("");
                    setDescription("");
                  }}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createDevice.isPending || !tenantId || !imei.trim()}
                  className="rounded-md bg-riot-gold-400 px-4 py-2 text-sm font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {createDevice.isPending ? "Adding..." : "Add device"}
                </button>
              </div>
              {addError && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{addError}</p>}
            </form>
          )}

          {serverQuery.blocked && (
            <p role="status" className="text-xs text-gray-500 dark:text-riot-navy-300">{serverQuery.blocked}</p>
          )}
          {adminDevicesQuery.isError && (
            <p role="alert" className="text-xs text-red-600 dark:text-red-400">
              Couldn&apos;t load devices: {extractErrorMessage(adminDevicesQuery.error)}
            </p>
          )}
          <DataTable
            title="All Devices"
            totalCount={total}
            columns={visibleColumns}
            data={tableData}
            keyExtractor={(d) => d.imei}
            selectable
            selectedKeys={selectedImeis}
            onSelectionChange={setSelectedImeis}
            serverSide
            serverSort={sort}
            onServerSortChange={(next: ServerSort | null) => {
              restartPaging();
              setSort(next as AdminDeviceSort | null);
            }}
            emptyMessage={emptyMessage}
            onFilterChange={(f) => {
              restartPaging();
              setTableFilters({
                search: f.search,
                searchField: f.searchField as TableFilterState["searchField"],
                status: f.status as TableFilterState["status"],
                tenantIds: f.multiSelect.tenantId ?? [],
                unallocated: !!f.checkbox.groupId,
                dateRanges: f.dateRanges,
              });
            }}
            pagination={{
              page: paging.page,
              totalPages,
              count: adminDevices.length,
              hasMore,
              rangeLabel,
              pageSize,
              pageSizeOptions: PAGE_SIZE_OPTIONS,
              onPageSizeChange: (size) => {
                restartPaging();
                setPageSize(size);
              },
              onNext: showNextPage,
              onPrev: showPreviousPage,
              onFirst: restartPaging,
            }}
            filters={{
              searchPlaceholder: "Search by",
              searchFields: tableSearchFields,
              // Only the freshness statuses the API reports can match.
              statusOptions: [
                { value: "online", label: "Online" },
                { value: "stale", label: "Stale" },
                { value: "offline", label: "Offline" },
              ],
              multiSelectFilters: tableMultiSelectFilters,
              dateRangeFilters: DEVICE_DATE_RANGE_FILTERS,
              checkboxFilters: [
                {
                  key: "groupId",
                  label: "Unallocated",
                  mode: "missing",
                },
              ],
            }}
            actions={
              <>
                <button
                  type="button"
                  onClick={() => {
                    if (scopedTenantId) {
                      setTenantId(scopedTenantId);
                      setCompanyQuery(scopedCompanyName ?? "");
                    }
                    setShowAddDevice(true);
                    setAddError("");
                  }}
                  className="flex items-center gap-1.5 rounded-md bg-riot-gold-400 px-3.5 py-1.5 text-xs font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600"
                >
                  <Plus size={14} /> Add device
                </button>
                <button
                  type="button"
                  onClick={handleCreateTaskForSelection}
                  disabled={selectedImeis.length === 0}
                  className="flex items-center gap-1.5 rounded-md bg-riot-gold-400 px-3.5 py-1.5 text-xs font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600 disabled:cursor-not-allowed disabled:opacity-45"
                  title={selectedImeis.length === 0 ? "Select one or more devices first" : "Create firmware task for selected devices"}
                >
                  <Rocket size={14} /> Create task{selectedImeis.length > 0 ? ` (${selectedImeis.length})` : ""}
                </button>
                <button
                  type="button"
                  onClick={handleUploadFileForSelection}
                  disabled={selectedImeis.length === 0}
                  className="flex items-center gap-1.5 rounded-md bg-riot-gold-400 px-3.5 py-1.5 text-xs font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600 disabled:cursor-not-allowed disabled:opacity-45"
                  title={selectedImeis.length === 0 ? "Select one or more devices first" : "Upload a firmware file for selected devices"}
                >
                  <Upload size={14} /> Add file{selectedImeis.length > 0 ? ` (${selectedImeis.length})` : ""}
                </button>
                <button
                  type="button"
                  onClick={handleShadowSettingsForSelection}
                  disabled={selectedImeis.length !== 1}
                  className="flex items-center gap-1.5 rounded-md bg-riot-gold-400 px-3.5 py-1.5 text-xs font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600 disabled:cursor-not-allowed disabled:opacity-45"
                  title={selectedImeis.length === 1 ? "Edit shadow settings for selected device" : "Select exactly one device first"}
                >
                  <Settings2 size={14} /> Shadow setting
                </button>
                <button
                  type="button"
                  onClick={() => serverQuery.filters && csvExport.start(adminDeviceFilterParams(serverQuery.filters))}
                  disabled={csvExport.running || !serverQuery.filters}
                  className="flex items-center gap-1.5 rounded-md border border-gray-300 px-3.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-45 dark:border-riot-navy-600 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
                  title="Export every device matching the filters to CSV"
                >
                  <Download size={14} /> {csvExport.running ? "Exporting…" : "Export"}
                </button>
                <div className="relative" ref={moveRef}>
                  <button
                    onClick={() => setMoveOpen((v) => !v)}
                    disabled={selectedImeis.length === 0}
                    className={`flex items-center gap-1 rounded-md border px-3.5 py-1.5 text-xs font-medium transition-colors ${
                      selectedImeis.length === 0
                        ? "cursor-not-allowed border-gray-200 text-gray-400 dark:border-riot-navy-700 dark:text-riot-navy-500"
                        : moveOpen
                          ? "border-riot-gold-400 bg-riot-gold-50 text-riot-navy-900 dark:border-riot-gold-500 dark:bg-riot-navy-700 dark:text-riot-gold-400"
                          : "border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
                    }`}
                  >
                    <MoveIcon size={12} />
                    Move {selectedImeis.length > 0 ? `(${selectedImeis.length})` : ""}
                    <ChevronDown size={12} className={moveOpen ? "rotate-180 transition-transform" : "transition-transform"} />
                  </button>
                  {moveOpen && (
                    <div className="absolute right-0 top-full z-10 mt-1 w-64 rounded-md border border-gray-200 bg-white py-2 shadow-lg dark:border-riot-navy-700 dark:bg-riot-navy-800">
                      <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-riot-navy-400">
                        Move to group
                      </div>
                      {groups.length === 0 ? (
                        <div className="px-3 py-1.5 text-xs text-gray-400 dark:text-riot-navy-400">
                          No groups available.
                        </div>
                      ) : (
                        groups.map((g) => (
                          <button
                            key={g.groupId}
                            onClick={() => applyMove({ groupId: g.groupId }, g.name)}
                            className="block w-full px-3 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-50 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
                          >
                            {g.name}
                            <span className="ml-1.5 text-[10px] text-gray-400 dark:text-riot-navy-400">({g.type})</span>
                          </button>
                        ))
                      )}
                      <button
                        onClick={() => applyMove({ groupId: null }, "no group")}
                        className="block w-full px-3 py-1.5 text-left text-xs text-gray-500 hover:bg-gray-50 dark:text-riot-navy-300 dark:hover:bg-riot-navy-700"
                      >
                        Clear group assignment
                      </button>

                      {!isScopedCompanyView && (
                        <>
                          <div className="my-1 border-t border-gray-100 dark:border-riot-navy-700" />

                          <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-riot-navy-400">
                            Move to tenant
                          </div>
                          {companies.length === 0 ? (
                            <div className="px-3 py-1.5 text-xs text-gray-400 dark:text-riot-navy-400">
                              No tenants available.
                            </div>
                          ) : (
                            companies.map((c) => (
                              <button
                                key={c.tenantId}
                                onClick={() => applyMove({ tenantId: c.tenantId, groupId: null }, c.companyName)}
                                className="block w-full px-3 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-50 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
                              >
                                {c.companyName}
                                <span className="ml-1.5 font-mono text-[10px] text-gray-400 dark:text-riot-navy-400">{c.tenantId}</span>
                              </button>
                            ))
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </>
            }
          />
        </div>
      )}
    </div>
  );
}
